import { supabase } from './supabase';
import { fetchAllRows } from './fetchAll';

/**
 * Resolving the ORIGINAL photo behind an already-approved venue photo.
 *
 * ── Why this exists ───────────────────────────────────────────────────────────
 * The app renders venue photos at 1:1 everywhere (`screens/venue-discovery.tsx`
 * `aspectRatio: 1`, city grid cards likewise). Everything approved before 2026-08 was composed
 * in a 4:3 crop box, so the app centre-cuts those crops and the framing sits wrong.
 *
 * Evan's call (2026-08-02): most rating photos were ALREADY square when the user uploaded them,
 * so for those the fix is not a re-crop at all — it is to point the approved photo back at the
 * user's untouched original. That is a pointer change: lossless, instant, no image processing.
 *
 * ── Where the original lives (verified against prod 2026-08-02) ───────────────
 *  - `venue_photos.source = 'rating_photo'` rows are written by the app's
 *    `lib/venuePhotos.ts` `submitRatingPhotosToVenuePool` — ONE row per entry of the rating's
 *    `logs.flash_media` array, all inserted in a single statement, `storage_path = ''` and
 *    `public_url` = the user's flash URL.
 *  - Approval OVERWRITES `public_url` with the cropped `admin-approved/…` file. The flash object
 *    itself survives: `venuePhotoStoragePath()` only resolves `/venue-photos/` URLs, so the
 *    storage delete in `VenueReviewScreen.approvePhoto` no-ops on a flashes URL.
 *  → The original is therefore recovered through `venue_photos.log_id → logs.flash_media[]`.
 *
 * ── The attribution problem (the reason this file is not trivial) ─────────────
 * When a rating carried several photos, that ONE log produced several `venue_photos` rows, and
 * approval erased the only link saying which row came from which `flash_media` entry:
 *   - the rows share an identical `created_at` (single batch insert), so time cannot order them;
 *   - the approved storage path is `admin-approved/{venue_id}/{photo_row_id}.jpg` — it encodes the
 *     ROW id, never the source filename;
 *   - `id` is a random uuid, and `ctid` is destroyed by the approval UPDATE.
 *
 * What IS recoverable is recovered by ELIMINATION: a `flash_media` entry whose URL is still held
 * by some OTHER row of the same log belongs to that row, so it is not a candidate for this one.
 * That yields four confidence levels — see {@link OriginalConfidence}. Anything below `exact`
 * must never be written without the admin looking at it, which is what the UI enforces.
 *
 * Prod counts at build time (2026-08-02): 246 approved rows / 155 of them live heroes.
 *   exact 102 (89 square) · set 120 (93 all-square) · ambiguous 7 · none 13 · already-original 1.
 */

const ID_CHUNK = 120;

export function chunkIds(ids: string[], size: number = ID_CHUNK): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

export function pickString(v: unknown): string | null {
  if (typeof v === 'string' && v.trim()) return v.trim();
  return null;
}

function pickInt(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.round(v);
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Math.round(Number(v));
  return null;
}

export function isFlashUrl(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.includes('/flashes/');
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Types                                                                      */
/* ────────────────────────────────────────────────────────────────────────── */

export type VenueLite = {
  id: string;
  venue_name_user_input: string | null;
  city_country: string | null;
  approved_venue_photo_url: string | null;
};

export type ProfileLite = { id: string; username: string | null; avatar_url: string | null };

/** One entry of `logs.flash_media` — the user's untouched upload. */
export type MediaEntry = { url: string; width: number | null; height: number | null };

export type OriginalShape = 'square' | 'nonsquare' | 'unknown';

/**
 * How confidently the original behind an approved photo can be named.
 *
 * - `exact`      — exactly one unclaimed `flash_media` entry. Unambiguous.
 * - `set`        — this log has N unclaimed entries and N approved rows. The SET of originals is
 *                  known and every row of a log belongs to the SAME venue, so restoring them all
 *                  puts the right images on the right venue. Only which one ends up the *hero*
 *                  (and the carousel order) may shuffle.
 * - `ambiguous`  — more unclaimed entries than approved rows. A restore could put a photo the
 *                  admin never approved on the venue. Manual pick only.
 * - `none`       — no `log_id` (pre-dates the column, or a direct `venue_upload`). Unrecoverable;
 *                  for `venue_upload` the source file was physically deleted at approval time.
 * - `alreadyOriginal` — `public_url` is still the flash URL; this row was approved without a crop.
 */
export type OriginalConfidence = 'exact' | 'set' | 'ambiguous' | 'none' | 'alreadyOriginal';

export type LivePhotoItem = {
  /** `venue_photos.id` */
  id: string;
  venueId: string;
  logId: string | null;
  userId: string | null;
  source: string | null;
  /** What the app serves today. */
  currentUrl: string;
  displayOrder: number | null;
  /** True when this row IS `venues.approved_venue_photo_url`. */
  isHero: boolean;
  /** When the photo was approved — drives the newest/oldest sort on the Live grid. */
  approvedAt: string | null;
  venue: VenueLite;
  name: string;
  city: string;
  confidence: OriginalConfidence;
  /** Every `flash_media` entry that could be this row's original (post-elimination). */
  candidates: MediaEntry[];
  /**
   * The entry this row would be restored to. Set for `exact` (the only candidate) and for `set`
   * (a stable url-sorted ↔ id-sorted pairing across the log's approved rows). Null otherwise.
   */
  proposed: MediaEntry | null;
  proposedShape: OriginalShape;
  /** Safe to restore with no human judgement: `exact`/`set` AND the proposed entry is square. */
  restorable: boolean;
};

export type PendingPhotoItem = {
  id: string;
  venueId: string;
  userId: string | null;
  logId: string | null;
  source: string | null;
  publicUrl: string;
  createdAt: string | null;
  venue: VenueLite | null;
  name: string;
  city: string;
  /** The venue already has `approved_venue_photo_url` — approving appends, never displaces. */
  venueHasPrimary: boolean;
  /** How many photos are already live on this venue. */
  liveCount: number;
  /** Dimensions from the parent rating's `flash_media`, when this row is still a flash URL. */
  shape: OriginalShape;
  width: number | null;
  height: number | null;
};

export type PhotoWorkspace = {
  pending: PendingPhotoItem[];
  live: LivePhotoItem[];
  profileById: Map<string, ProfileLite>;
};

/* ────────────────────────────────────────────────────────────────────────── */
/* Pure classification                                                        */
/* ────────────────────────────────────────────────────────────────────────── */

export function shapeOf(entry: MediaEntry | null | undefined): OriginalShape {
  if (!entry || entry.width == null || entry.height == null) return 'unknown';
  return entry.width === entry.height ? 'square' : 'nonsquare';
}

export function mediaFromLog(flashMedia: unknown): MediaEntry[] {
  if (!Array.isArray(flashMedia)) return [];
  const out: MediaEntry[] = [];
  for (const raw of flashMedia) {
    if (!raw || typeof raw !== 'object') continue;
    const url = pickString((raw as { url?: unknown }).url);
    if (!url) continue;
    out.push({
      url,
      width: pickInt((raw as { width?: unknown }).width),
      height: pickInt((raw as { height?: unknown }).height),
    });
  }
  // De-dupe by url — a malformed array must never inflate the candidate count.
  const seen = new Set<string>();
  return out.filter((e) => (seen.has(e.url) ? false : (seen.add(e.url), true)));
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Loader                                                                     */
/* ────────────────────────────────────────────────────────────────────────── */

type PhotoRow = {
  id: string;
  venue_id: string;
  user_id: string | null;
  log_id: string | null;
  public_url: string | null;
  source: string | null;
  approved: boolean;
  rejected: boolean;
  display_order: number | null;
  created_at: string | null;
  approved_at: string | null;
};

const PHOTO_SELECT =
  'id, venue_id, user_id, log_id, public_url, source, approved, rejected, display_order, created_at, approved_at';

async function fetchVenuesByIds(ids: string[]): Promise<Map<string, VenueLite>> {
  const out = new Map<string, VenueLite>();
  for (const chunk of chunkIds(ids)) {
    const { data, error } = await supabase
      .from('venues')
      .select('id, venue_name_user_input, city_country, approved_venue_photo_url')
      .in('id', chunk);
    if (error) throw error;
    for (const v of (data ?? []) as VenueLite[]) out.set(v.id, v);
  }
  return out;
}

/** Defensive: a missing grant on `profiles` must never break the page. */
async function fetchProfilesByIds(ids: string[]): Promise<Map<string, ProfileLite>> {
  const out = new Map<string, ProfileLite>();
  for (const chunk of chunkIds(ids)) {
    try {
      const { data, error } = await supabase.from('profiles').select('id, username, avatar_url').in('id', chunk);
      if (error) throw error;
      for (const p of (data ?? []) as ProfileLite[]) if (p?.id) out.set(p.id, p);
    } catch {
      /* ignore — submitter name is cosmetic */
    }
  }
  return out;
}

/**
 * One round trip set for the whole screen: every approved photo (to restore / re-crop) and every
 * pending photo (the approval queue), plus the sibling rows and ratings needed to resolve originals.
 * Read-only — this function never writes.
 */
export async function loadPhotoWorkspace(): Promise<PhotoWorkspace> {
  // Two plain queries rather than one compound `.or(...)` — the filters stay obvious, and neither
  // depends on PostgREST's nested-boolean grammar.
  const [approved, pendingRows] = await Promise.all([
    fetchAllRows<PhotoRow>((from, to) =>
      supabase.from('venue_photos').select(PHOTO_SELECT).eq('approved', true).range(from, to),
    ),
    fetchAllRows<PhotoRow>((from, to) =>
      supabase
        .from('venue_photos')
        .select(PHOTO_SELECT)
        .eq('approved', false)
        .eq('rejected', false)
        .range(from, to),
    ),
  ]);

  // Sibling rows of every log we care about — an unapproved sibling still holding its flash URL is
  // what lets us ELIMINATE that entry from another row's candidate list.
  const logIds = Array.from(
    new Set(
      [...approved, ...pendingRows].map((p) => pickString(p.log_id)).filter((v): v is string => Boolean(v)),
    ),
  );

  const siblingsByLog = new Map<string, PhotoRow[]>();
  for (const chunk of chunkIds(logIds)) {
    const { data, error } = await supabase.from('venue_photos').select(PHOTO_SELECT).in('log_id', chunk);
    if (error) throw error;
    for (const row of (data ?? []) as PhotoRow[]) {
      const key = pickString(row.log_id);
      if (!key) continue;
      const list = siblingsByLog.get(key);
      if (list) list.push(row);
      else siblingsByLog.set(key, [row]);
    }
  }

  const mediaByLog = new Map<string, MediaEntry[]>();
  for (const chunk of chunkIds(logIds)) {
    const { data, error } = await supabase.from('logs').select('id, flash_media').in('id', chunk);
    if (error) throw error;
    for (const row of (data ?? []) as { id: string; flash_media: unknown }[]) {
      mediaByLog.set(row.id, mediaFromLog(row.flash_media));
    }
  }

  const venueIds = Array.from(new Set([...approved, ...pendingRows].map((p) => p.venue_id).filter(Boolean)));
  const venueById = await fetchVenuesByIds(venueIds);

  const profileById = await fetchProfilesByIds(
    [...approved, ...pendingRows].map((p) => pickString(p.user_id)).filter((v): v is string => Boolean(v)),
  );

  const liveCountByVenue = new Map<string, number>();
  for (const p of approved) liveCountByVenue.set(p.venue_id, (liveCountByVenue.get(p.venue_id) ?? 0) + 1);

  /* ── Live (approved) items ─────────────────────────────────────────────── */
  const live: LivePhotoItem[] = [];
  for (const row of approved) {
    const currentUrl = pickString(row.public_url);
    const venue = venueById.get(row.venue_id);
    if (!currentUrl || !venue) continue;

    const logId = pickString(row.log_id);
    const media = logId ? (mediaByLog.get(logId) ?? []) : [];
    const siblings = logId ? (siblingsByLog.get(logId) ?? []) : [];

    // Elimination: drop any entry another row of this log still points at.
    const claimed = new Set(
      siblings
        .filter((s) => s.id !== row.id)
        .map((s) => pickString(s.public_url))
        .filter((v): v is string => Boolean(v)),
    );
    const candidates = media.filter((m) => !claimed.has(m.url));

    const approvedSiblingIds = siblings
      .filter((s) => s.approved)
      .map((s) => s.id)
      .sort();

    let confidence: OriginalConfidence;
    let proposed: MediaEntry | null = null;

    if (isFlashUrl(currentUrl)) {
      confidence = 'alreadyOriginal';
    } else if (candidates.length === 0) {
      confidence = 'none';
    } else if (candidates.length === 1) {
      confidence = 'exact';
      proposed = candidates[0];
    } else if (candidates.length === approvedSiblingIds.length) {
      confidence = 'set';
      // Stable bijection: url-sorted candidates ↔ id-sorted approved rows. Arbitrary but
      // deterministic, and every row of a log shares one venue, so the venue's photo SET is right
      // whichever way the pairing falls.
      const idx = approvedSiblingIds.indexOf(row.id);
      const ordered = [...candidates].sort((a, b) => a.url.localeCompare(b.url));
      proposed = idx >= 0 ? (ordered[idx] ?? null) : null;
      if (!proposed) confidence = 'ambiguous';
    } else {
      confidence = 'ambiguous';
    }

    const proposedShape = shapeOf(proposed);
    live.push({
      id: row.id,
      venueId: row.venue_id,
      approvedAt: row.approved_at ?? null,
      logId,
      userId: pickString(row.user_id),
      source: pickString(row.source),
      currentUrl,
      displayOrder: row.display_order,
      isHero: pickString(venue.approved_venue_photo_url) === currentUrl,
      venue,
      name: pickString(venue.venue_name_user_input) ?? 'Untitled venue',
      city: pickString(venue.city_country) ?? '—',
      confidence,
      candidates,
      proposed,
      proposedShape,
      restorable: (confidence === 'exact' || confidence === 'set') && proposedShape === 'square',
    });
  }

  /* ── Pending items ─────────────────────────────────────────────────────── */
  const pending: PendingPhotoItem[] = [];
  for (const row of pendingRows) {
    const publicUrl = pickString(row.public_url);
    if (!publicUrl) continue;
    const venue = venueById.get(row.venue_id) ?? null;
    const logId = pickString(row.log_id);
    const media = logId ? (mediaByLog.get(logId) ?? []) : [];
    const own = media.find((m) => m.url === publicUrl) ?? null;

    pending.push({
      id: row.id,
      venueId: row.venue_id,
      userId: pickString(row.user_id),
      logId,
      source: pickString(row.source),
      publicUrl,
      createdAt: row.created_at,
      venue,
      name: pickString(venue?.venue_name_user_input ?? null) ?? 'Untitled venue',
      city: pickString(venue?.city_country ?? null) ?? '—',
      venueHasPrimary: pickString(venue?.approved_venue_photo_url ?? null) != null,
      liveCount: liveCountByVenue.get(row.venue_id) ?? 0,
      shape: shapeOf(own),
      width: own?.width ?? null,
      height: own?.height ?? null,
    });
  }

  pending.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
  live.sort((a, b) => a.name.localeCompare(b.name) || (a.displayOrder ?? 99) - (b.displayOrder ?? 99));

  return { pending, live, profileById };
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bulk-restore planning (pure — produces the plan the confirm modal shows)    */
/* ────────────────────────────────────────────────────────────────────────── */

export type RestoreTier = 'exact' | 'set';

export type RestorePlanEntry = {
  item: LivePhotoItem;
  tier: RestoreTier;
  /** The flash URL that `public_url` (and, for a hero, `approved_venue_photo_url`) becomes. */
  targetUrl: string;
};

/**
 * The exact set of rows a bulk restore would touch, given which tiers the admin ticked.
 * Nothing here writes — the caller shows this, then runs {@link import('./venuePhotoApproval')}.
 */
export function buildRestorePlan(items: LivePhotoItem[], tiers: readonly RestoreTier[]): RestorePlanEntry[] {
  const wanted = new Set<RestoreTier>(tiers);
  const out: RestorePlanEntry[] = [];
  for (const item of items) {
    if (!item.restorable || !item.proposed) continue;
    const tier = item.confidence === 'exact' ? 'exact' : 'set';
    if (item.confidence !== 'exact' && item.confidence !== 'set') continue;
    if (!wanted.has(tier)) continue;
    out.push({ item, tier, targetUrl: item.proposed.url });
  }
  return out;
}
